import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { CHART_COLORS } from './colors';

/**
 * Extracted from CategoryConsole.js (component-split refactor, round 3).
 * Hierarchy tab: level selector, pyramid visualization, and summary stats.
 * `selectedLevel` stays controlled by the parent because the Segmentation
 * tab and the CSV/PDF exports share the same level state.
 */
export function HierarchyTab({ hierarchy, selectedLevel, setSelectedLevel, cs }) {
  return (
    <TabsContent value="hierarchy">
      <Card>
        <CardHeader>
          <CardTitle>Category Hierarchy - Pyramid View</CardTitle>
          <CardDescription>Select a level to view spend distribution</CardDescription>
          {/* Level Selector */}
          <div className="flex gap-2 mt-4">
            {['Level 1', 'Level 2', 'Level 3'].map((level, idx) => (
              <Button
                key={level}
                variant={selectedLevel === `level_${idx + 1}` ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedLevel(`level_${idx + 1}`)}
                data-testid={`hierarchy-level-${idx + 1}`}
              >
                {level}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {hierarchy?.hierarchy?.length > 0 ? (
            <div className="space-y-4">
              {/* Single Pyramid Visualization */}
              <div className="flex flex-col items-center">
                {(() => {
                  // Get data for selected level
                  let levelData = [];
                  let levelLabel = '';

                  if (selectedLevel === 'level_1') {
                    levelData = hierarchy.hierarchy.map(cat => ({
                      name: cat.name,
                      spend: cat.spend,
                      transaction_count: cat.transaction_count,
                      supplier_count: cat.supplier_count,
                      parent: null
                    }));
                    levelLabel = 'Level 1 - Main Categories';
                  } else if (selectedLevel === 'level_2') {
                    levelData = hierarchy.hierarchy.flatMap(cat =>
                      (cat.children || []).map(child => ({
                        name: child.name,
                        spend: child.spend,
                        transaction_count: child.transaction_count,
                        supplier_count: child.supplier_count,
                        parent: cat.name
                      }))
                    );
                    levelLabel = 'Level 2 - Sub-Categories';
                  } else if (selectedLevel === 'level_3') {
                    levelData = hierarchy.hierarchy.flatMap(cat =>
                      (cat.children || []).flatMap(child =>
                        (child.children || []).filter(gc => gc.name && gc.name !== '0' && gc.name !== null).map(grandchild => ({
                          name: grandchild.name,
                          spend: grandchild.spend,
                          transaction_count: grandchild.transaction_count,
                          supplier_count: grandchild.supplier_count,
                          parent: `${cat.name} → ${child.name}`
                        }))
                      )
                    );
                    levelLabel = 'Level 3 - Detailed Categories';
                  }

                  // Sort by spend descending
                  levelData.sort((a, b) => b.spend - a.spend);
                  const totalSpend = levelData.reduce((sum, d) => sum + d.spend, 0);

                  // Create pyramid rows - more items at top, fewer at bottom
                  const createPyramidRows = (data) => {
                    if (data.length === 0) return [];
                    const rows = [];
                    let remaining = [...data];
                    let rowSize = Math.min(Math.ceil(data.length / 3) + 2, 8); // Start with larger row

                    while (remaining.length > 0) {
                      rows.push(remaining.splice(0, rowSize));
                      rowSize = Math.max(1, rowSize - 1); // Decrease row size for pyramid effect
                    }
                    return rows;
                  };

                  const pyramidRows = createPyramidRows(levelData);
                  const maxRowWidth = 100; // percentage

                  return (
                    <div className="w-full max-w-4xl" data-testid="hierarchy-pyramid">
                      <p className="text-sm font-semibold text-center text-muted-foreground mb-4">{levelLabel}</p>
                      <p className="text-xs text-center text-muted-foreground mb-6">
                        {levelData.length} categories • Total: {cs}{(totalSpend / 1000000).toFixed(1)}M
                      </p>

                      <div className="flex flex-col items-center gap-2">
                        {pyramidRows.map((row, rowIndex) => {
                          const rowWidth = maxRowWidth - (rowIndex * 8); // Decrease width for each row
                          return (
                            <div
                              key={rowIndex}
                              className="flex justify-center gap-1 flex-wrap"
                              style={{ width: `${rowWidth}%` }}
                            >
                              {row.map((item, itemIndex) => {
                                const percentage = ((item.spend / totalSpend) * 100).toFixed(1);
                                const globalIndex = pyramidRows.slice(0, rowIndex).reduce((sum, r) => sum + r.length, 0) + itemIndex;

                                return (
                                  <div
                                    key={`${item.name}-${globalIndex}`}
                                    className="relative group cursor-pointer transition-all hover:scale-105 hover:z-10 flex-1"
                                    style={{ minWidth: '80px', maxWidth: '180px' }}
                                  >
                                    <div
                                      className="h-16 rounded-lg flex flex-col items-center justify-center text-white px-2 text-center shadow-md"
                                      style={{ backgroundColor: CHART_COLORS[globalIndex % CHART_COLORS.length] }}
                                    >
                                      <span className="text-xs font-semibold truncate w-full">
                                        {item.name?.length > 18 ? item.name.slice(0, 18) + '...' : item.name}
                                      </span>
                                      <span className="text-[10px] opacity-90">
                                        {cs}{(item.spend / 1000000).toFixed(1)}M
                                      </span>
                                    </div>
                                    {/* Tooltip */}
                                    <div className="absolute hidden group-hover:block bg-popover border rounded-lg shadow-xl p-3 z-30 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap">
                                      <p className="font-semibold text-sm">{item.name}</p>
                                      {item.parent && (
                                        <p className="text-xs text-muted-foreground mb-1">Parent: {item.parent}</p>
                                      )}
                                      <div className="border-t pt-2 mt-2 space-y-1">
                                        <p className="text-sm">Spend: <span className="font-mono font-semibold">{cs}{item.spend?.toLocaleString()}</span></p>
                                        <p className="text-sm">Share: <span className="font-semibold">{percentage}%</span></p>
                                        <p className="text-sm">Transactions: <span className="font-mono">{item.transaction_count}</span></p>
                                        <p className="text-sm">Suppliers: <span className="font-mono">{item.supplier_count}</span></p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}

                        {/* Pyramid Point */}
                        <div className="mt-2">
                          <div className="w-0 h-0 border-l-[60px] border-r-[60px] border-t-[30px] border-l-transparent border-r-transparent border-t-muted-foreground/30"></div>
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="mt-8 pt-4 border-t">
                        <p className="text-xs text-muted-foreground text-center mb-3">Top Categories by Spend</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {levelData.slice(0, 10).map((item, index) => (
                            <div key={`${item.name}-${index}`} className="flex items-center gap-1.5 text-xs">
                              <div
                                className="w-3 h-3 rounded-sm shrink-0"
                                style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                              />
                              <span className="text-muted-foreground">
                                {item.name?.length > 15 ? item.name.slice(0, 15) + '...' : item.name} ({cs}{(item.spend / 1000000).toFixed(1)}M)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div
                  className={`text-center p-3 rounded-lg cursor-pointer transition-colors ${selectedLevel === 'level_1' ? 'bg-primary/10 border border-primary' : 'hover:bg-muted/50'}`}
                  onClick={() => setSelectedLevel('level_1')}
                  data-testid="hierarchy-stat-level-1"
                >
                  <p className="text-lg font-bold">{hierarchy.hierarchy.length}</p>
                  <p className="text-xs text-muted-foreground">Level 1 Categories</p>
                </div>
                <div
                  className={`text-center p-3 rounded-lg cursor-pointer transition-colors ${selectedLevel === 'level_2' ? 'bg-primary/10 border border-primary' : 'hover:bg-muted/50'}`}
                  onClick={() => setSelectedLevel('level_2')}
                  data-testid="hierarchy-stat-level-2"
                >
                  <p className="text-lg font-bold">
                    {hierarchy.hierarchy.reduce((sum, c) => sum + (c.children?.length || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Level 2 Sub-Categories</p>
                </div>
                <div
                  className={`text-center p-3 rounded-lg cursor-pointer transition-colors ${selectedLevel === 'level_3' ? 'bg-primary/10 border border-primary' : 'hover:bg-muted/50'}`}
                  onClick={() => setSelectedLevel('level_3')}
                  data-testid="hierarchy-stat-level-3"
                >
                  <p className="text-lg font-bold">
                    {hierarchy.hierarchy.reduce((sum, c) =>
                      sum + (c.children || []).reduce((s, ch) => s + (ch.children?.filter(gc => gc.name && gc.name !== '0' && gc.name !== null)?.length || 0), 0), 0
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">Level 3 Categories</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No hierarchy data available</p>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
